import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { cn } from '@/lib/utils';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { RoomTypeEnum } from '@danda/shared';
import ChatPanel from '@/components/panels/ChatPanel';
import ScenePanel from '@/components/panels/ScenePanel';
import CharDetailsPanel from '@/components/panels/CharDetailsPanel';
import ModulePanel from '@/components/panels/ModulePanel';
import PartyPanel from '@/components/panels/PartyPanel';
import FriendsPanel from '@/components/panels/FriendsPanel';
import WelcomePanel from '@/components/panels/WelcomePanel';
import AdventureCtaPanel from '@/components/panels/AdventureCtaPanel';
import BeginAdventureOverlay from '@/components/lobby/BeginAdventureOverlay';
import { ReconnectScreen } from '@/components/common/ReconnectScreen';
import { DisconnectedOverlay } from '@/components/common/DisconnectedOverlay';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import PromoBanner from '@/components/promo/PromoBanner';
import LobbyBackground from '@/components/lobby/LobbyBackground';
import { websocketService } from '@/lib/websocket-service';
import { gameConnectionManager } from '@/lib/game-connection-manager';
import { apiClient } from '@/lib/api';
import { useLobbyStore, type Character } from '@/lib/stores/lobbyStore';
import { mergeApiCharactersWithStore } from './lobby-layout-merge';
import { resolveDuelParticipants } from './lobby-layout-duel';
import { findFreshCancelledNotification } from './lobby-layout-cancelled-notification';
import { useGameStore } from '@/lib/stores/gameStore';
import type { BackgroundRoomContext } from '@danda/shared';
import { getModuleHeroUrl, getModuleHeroVideoUrl, getVideoUrl } from '@/lib/media';
import ManagedVideo from '@/components/common/ManagedVideo';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { useAuthStore } from '@/lib/stores/authStore';
import { trackReachedLobbyOnce } from '@/lib/analytics';
import { RESUME_AUTO_START_KEY } from '@/lib/resume-card';
import { usePlayerActivityStore } from '@/lib/stores/playerActivityStore';
import { toast } from '@/lib/toast';

// `> 0` instead of `||` so VITE_…=0 doesn't silently fall back to the default
// (0 is falsy → || would skip it). Negative/NaN also falls through to default.
// Matches GAME_IDLE_TIMEOUT_MS so a player gets the same grace on either
// surface.
const LOBBY_IDLE_TIMEOUT_MS =
  Number(import.meta.env.VITE_LOBBY_IDLE_TIMEOUT_MS) > 0
    ? Number(import.meta.env.VITE_LOBBY_IDLE_TIMEOUT_MS)
    : 10 * 60 * 1000;

const LOCAL_STORAGE_LAYOUT_KEY = 'lobby-layout';

const PanelErrorFallback = (
  <div className="flex items-center justify-center h-full p-4 text-muted-foreground text-sm">
    <p>Something went wrong. Try refreshing the page.</p>
  </div>
);

export default function LobbyLayout() {
  // Dismiss the session-ending toast that persists across route changes (duration: Infinity)
  useEffect(() => {
    toast.dismiss('session-ending');
  }, []);

  // Activation conversion: reaching the lobby = the player started playing.
  // Deepest goal of the paid funnel (click → signup → lobby). Fired once per
  // browser; no-ops for the X pixel unless the campaign env vars are set.
  useEffect(() => {
    trackReachedLobbyOnce();
  }, []);

  const navigate = useNavigate();
  const [layout, setLayout] = useState<number[] | undefined>(() => {
    try {
      const storedLayout = localStorage.getItem(LOCAL_STORAGE_LAYOUT_KEY);
      return storedLayout ? JSON.parse(storedLayout) : undefined;
    } catch (error) {
      console.error('Failed to parse stored layout from localStorage', error);
      return undefined;
    }
  });

  // Lobby layout per spec (lines 249-262):
  // Left: Scene Image (top) + Vitals Panel (bottom) - stacked vertically
  // Middle: Chat Panel (main interface - never closeable)
  // Right: Loadout Panel
  // Default: [25, 50, 25] - Left (25%), Chat (50%), Right (25%)
  const defaultLayout = useMemo(() => [25, 50, 25], []);

  // Seeded true when the dashboard's "Continue" CTA dropped a still-fresh
  // auto-start handoff flag (see the resumeAutoStartFired effect below), so
  // the portal overlay covers the lobby from the very first render instead
  // of leaving a gap between lobby-init and the WS round-trip where the full
  // lobby chrome (party panels, chat) would otherwise flash before
  // startGame's response arrives. Mirrors the consumer's own TTL check below
  // so a leftover unconsumed flag (e.g. user left /lobby pre-init and
  // returned later in the same tab) doesn't seed a loading state that's
  // already past its TTL.
  const [isGameTransitioning, setIsGameTransitioning] = useState(() => {
    const raw = sessionStorage.getItem(RESUME_AUTO_START_KEY);
    if (!raw) return false;
    const age = Date.now() - Number(raw);
    return !Number.isNaN(age) && age <= 60_000;
  });
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [gameStartError, setGameStartError] = useState<string | null>(null);
  const [portalBgLoaded, setPortalBgLoaded] = useState(false);

  // Lobby idle: after LOBBY_IDLE_TIMEOUT_MS with no player intent, return to
  // /dashboard. "Player intent" = outbound WS message of a non-protocol type
  // (chat, party action, character action) — NOT DOM mouse/scroll, because
  // NPC narration auto-scrolls the chat panel and would otherwise hold the
  // timer open forever (#284 surface).
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const lastPlayerIntentAt = usePlayerActivityStore((s) => s.lastPlayerIntentAt);
  const { resetTimer } = useInactivityTimeout({
    enabled: isLoggedIn,
    timeoutMs: LOBBY_IDLE_TIMEOUT_MS,
    // Default DOM activity events reset the timer: browsing the lobby,
    // reading chats, or hovering the roster is presence. With events []
    // the watchdog bounced active users to /dashboard (and dropped their
    // lobby presence) 5 minutes after their last CHAT message.
    // Defense-in-depth: if the lobby WS init wedges, the idle timer would
    // otherwise fire and bounce the user to /dashboard before they ever see a
    // usable lobby. Pausing while `loading` is true keeps them on the
    // LobbyOpeningPortal screen — which has its own progressive messaging —
    // instead of yanking them out to dashboard mid-handshake.
    shouldPauseTimeout: () =>
      isCreatingSession || isGameTransitioning || useLobbyStore.getState().isLoading,
    onTimeout: () => {
      useLobbyStore.getState().reset();
      apiClient.clearAll();
      toast.info('You were idle, returning to dashboard.');
      navigate('/dashboard');
    },
  });
  useEffect(() => {
    resetTimer();
  }, [lastPlayerIntentAt, resetTimer]);
  const [transitionDuelParticipants, setTransitionDuelParticipants] = useState<
    | {
        player?: { charId?: string; name?: string; imageUrl?: string };
        opponent?: { charId?: string; name?: string; imageUrl?: string };
      }
    | undefined
  >(undefined);

  const {
    setCharacters,
    setLoading,
    setError,
    selectedCharacterId,
    selectCharacter,
    notifications,
    markNotificationRead,
    isInitialized,
    characters,
    lobbyCharacters,
    activeChatRoomId,
    chatRooms,
    reconnectFailed,
    isOnline,
    currentPartyId,
    pendingGameStart,
    setPendingGameStart,
  } = useLobbyStore();

  // Resolve duel-loader portraits against the live lobby store, mirroring
  // GameLayout's `resolveUrl`. The raw `transitionDuelParticipants` snapshot
  // captures whatever URLs the server (or our /duel pre-write) had at start
  // time — but session.portrait_url for the opponent may only arrive a tick
  // later via `lobby:session-state`. Without re-resolving, the loader shows
  // the opponent's name with no face. We rebuild the participants object
  // whenever the raw snapshot OR the underlying lobby characters change.
  const resolvedDuelParticipants = useMemo(
    () => resolveDuelParticipants(transitionDuelParticipants, characters, lobbyCharacters),
    [transitionDuelParticipants, characters, lobbyCharacters]
  );

  const activeRoom = chatRooms.find((r) => r.id === activeChatRoomId);

  // Build room context for background image generation
  // The server will generate prompts from this context (prevents prompt injection)
  const roomContext = useMemo((): BackgroundRoomContext | undefined => {
    if (!activeRoom) {
      return { roomType: 'lobby' };
    }

    const roomType = activeRoom.type;

    if (roomType === RoomTypeEnum.TAVIAN) {
      return { roomType: RoomTypeEnum.TAVIAN };
    }

    if (roomType === RoomTypeEnum.ALDRIC) {
      return { roomType: RoomTypeEnum.ALDRIC };
    }

    if (roomType === RoomTypeEnum.KIRA) {
      return { roomType: RoomTypeEnum.KIRA };
    }

    if (roomType === RoomTypeEnum.GROLT) {
      return { roomType: RoomTypeEnum.GROLT };
    }

    // Inner Voice tab — use character portrait as background (no generation needed)
    if (roomType === RoomTypeEnum.INNER) {
      return undefined; // Background handled via portrait effect below
    }

    if (roomType === RoomTypeEnum.PARTY) {
      // Extract party ID from room ID (format: "party-{partyId}")
      const partyId = activeRoom.id.replace('party-', '');
      return { roomType: RoomTypeEnum.PARTY, partyId };
    }

    // Private (1:1) chat — handled locally below via the target's portrait URL.
    // Server-side generation is gated on user_characters and rejects NPCs, so we
    // short-circuit the same way inner-voice does.
    if (roomType === RoomTypeEnum.PRIVATE) {
      return undefined;
    }

    return { roomType: RoomTypeEnum.LOBBY };
  }, [activeRoom]);

  // Inner Voice tab — show character portrait as background.
  // Cleanup nulls the store on room switch so we don't claim ownership of the
  // bg once the user has moved on; downstream consumers can rely on the
  // invariant "backgroundMediaUrl is set only while the owning effect is
  // active".
  const { setBackgroundMedia } = useLobbyStore();
  useEffect(() => {
    if (activeRoom?.type !== RoomTypeEnum.INNER) return;
    const char = characters.find((c) => c.id === selectedCharacterId);
    const portraitUrl = char?.session?.portrait_url ?? char?.avatarUrl;
    if (!portraitUrl) return;
    setBackgroundMedia(portraitUrl, 'image', null);
    return () => {
      setBackgroundMedia(null, null, null);
    };
  }, [activeRoom?.type, selectedCharacterId, characters, setBackgroundMedia]);

  // Private 1:1 chat — show the target character's portrait as background.
  // Mirrors the inner-voice short-circuit: same data path click-to-enlarge
  // uses, same cleanup contract on room switch.
  useEffect(() => {
    if (activeRoom?.type !== RoomTypeEnum.PRIVATE) return;
    const targetId = activeRoom.participants?.find((p) => p && p !== selectedCharacterId);
    if (!targetId) return;
    const lobbyChar = lobbyCharacters.find((c) => c.id === targetId);
    const storeChar = characters.find((c) => c.id === targetId);
    const portraitUrl =
      lobbyChar?.portraitUrl ??
      storeChar?.session?.portrait_url ??
      storeChar?.portraitUrl ??
      lobbyChar?.avatarUrl ??
      storeChar?.avatarUrl;
    if (!portraitUrl) return;
    setBackgroundMedia(portraitUrl, 'image', null);
    return () => {
      setBackgroundMedia(null, null, null);
    };
  }, [
    activeRoom?.type,
    activeRoom?.id,
    activeRoom?.participants,
    selectedCharacterId,
    lobbyCharacters,
    characters,
    setBackgroundMedia,
  ]);

  // Check if we have an actual character with loaded data (not just a persisted ID)
  // We require actual character data to be present before showing the full layout
  // This prevents layout jump where CharDetailsPanel renders as null initially
  const hasCharacter = selectedCharacterId && characters.some((c) => c.id === selectedCharacterId);

  // Load initial data from API with retry (3 attempts, exponential backoff)
  const loadInitialData = useCallback(
    async (attempt = 0) => {
      const MAX_RETRIES = 3;
      try {
        setLoading(true);
        setError(null);

        // Load characters
        const { characters: charData } = await apiClient.getCharacters();

        // Transform API response to frontend Character format.
        // Avatar URL lives in session (not permanent), arrives via lobby:session-state.
        // Until then, AvatarFallback renders the emoji — leave avatar undefined here so
        // a stale 404 placeholder isn't stamped over later real updates.
        //
        // Merge against any existing store entries so a WS `lobby:session-state`
        // hydration that landed first isn't clobbered. The race surface: user
        // returns from a duel → game-end teardown re-mounts LobbyLayout → WS
        // restores `session.equipped_slots` etc. from the snapshot, then this
        // /api/characters response (permanent-only) lands a tick later and a
        // hard replace would nuke `session`, leaving char-sheet panels reading
        // an empty equipment loadout.
        const characters: Character[] = mergeApiCharactersWithStore(
          useLobbyStore.getState().characters,
          charData
        );

        setCharacters(characters);

        // Validate persisted selectedCharacterId — clear if character was deleted
        const currentSelectedCharacterId = useLobbyStore.getState().selectedCharacterId;
        if (
          currentSelectedCharacterId &&
          !characters.some((c) => c.id === currentSelectedCharacterId)
        ) {
          console.warn(
            '[LobbyLayout] Persisted selectedCharacterId no longer valid, clearing:',
            currentSelectedCharacterId
          );
          selectCharacter(characters.length > 0 ? characters[0].id : null);
        } else if (!currentSelectedCharacterId && characters.length > 0) {
          selectCharacter(characters[0].id);
        }
      } catch (error) {
        console.error(
          `Failed to load initial lobby data (attempt ${attempt + 1}/${MAX_RETRIES}):`,
          error
        );
        if (attempt < MAX_RETRIES - 1) {
          const delay = 1000 * Math.pow(2, attempt); // 1s, 2s, 4s
          console.log(`[LobbyLayout] Retrying in ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return loadInitialData(attempt + 1);
        }
        setError(error instanceof Error ? error.message : 'Failed to load data');
      } finally {
        // Ensure loading is cleared after final attempt (success handled by isInitialized effect)
        if (attempt >= MAX_RETRIES - 1) {
          setLoading(false);
        }
      }
    },
    [setCharacters, setLoading, setError, selectCharacter]
  );

  const handleLayout = (sizes: number[]) => {
    setLayout(sizes);
    try {
      localStorage.setItem(LOCAL_STORAGE_LAYOUT_KEY, JSON.stringify(sizes));
    } catch (error) {
      console.error('Failed to save layout to localStorage', error);
    }
  };
  // Use effect to apply default layout if no stored layout is found on initial render
  useEffect(() => {
    if (layout === undefined) {
      setLayout(defaultLayout);
    }
  }, [layout, defaultLayout]);

  // Ref to track initialization timeout for proper cleanup
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize data and WebSocket connection when lobby loads
  useEffect(() => {
    const initializeLobby = async () => {
      console.log('[LobbyLayout] initializeLobby starting');
      // Reset the init-completion flags so each /lobby visit shows the
      // LobbyOpeningPortal until the new WS delivers fresh `lobby:updated`
      // and `lobby:session-state`. Without this, the flags are sticky from
      // the previous session — second mount renders the full lobby UI
      // immediately with stale parties/characters from the old store, and
      // if the new WS wedges (Cloud Run cold start, transient failure) the
      // user is stranded staring at stale data with no recovery path other
      // than reloading the page.
      const lobbyState = useLobbyStore.getState();
      lobbyState.setInitialized(false);
      lobbyState.setLobbyUpdatedReceived(false);
      lobbyState.setSessionStateReceived(false);
      setLoading(true);
      setError(null);

      // Set timeout - stored in ref for cleanup on unmount
      initTimeoutRef.current = setTimeout(() => {
        if (!useLobbyStore.getState().isInitialized) {
          setError('Lobby initialization timed out. Please refresh the page.');
          setLoading(false);
        }
      }, 10000); // 10 seconds timeout

      try {
        // Run the API character fetch and the WS auth+connect in parallel —
        // they're fully independent. Sequential awaits added the API RTT
        // (~200–800ms) on top of the WS handshake on every cold lobby load.
        // loadInitialData swallows its own errors, so only WS connect failures
        // surface in this catch.
        console.log('[LobbyLayout] Loading initial data + connecting WS...');
        const defaultLobbyId = 'lobby';
        await Promise.all([loadInitialData(), websocketService.connect(defaultLobbyId)]);
        console.log('[LobbyLayout] Done!');
      } catch (error) {
        // "Connection superseded" is a benign race during navigation/StrictMode
        // double-mount — the next attempt has already started, so this rejection
        // is expected. Skip the visible error state for it.
        const msg = error instanceof Error ? error.message : String(error);
        if (msg === 'Connection superseded') {
          console.debug('[LobbyLayout] WS connect superseded by newer attempt');
        } else {
          console.error('Failed to connect to lobby WebSocket:', error);
          setError(msg || 'Failed to connect to lobby');
          setLoading(false);
        }
      } finally {
        // Clear timeout if initialization completes (or fails) before timeout
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
          initTimeoutRef.current = null;
        }
      }
    };

    initializeLobby();

    // Cleanup on unmount
    return () => {
      // Clear initialization timeout to prevent memory leak
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      websocketService.disconnect();
      if (useGameStore.getState().connectionStatus.status === 'connected') {
        gameConnectionManager.endSession();
      }
    };
  }, [loadInitialData, setLoading, setError]); // Removed isInitialized - accessed via getState()

  // When the lobby is fully initialized (API + WS + initial lobby:updated event),
  // set loading to false.
  // Per BACKEND_FRONTEND_CONFORMANCE_GUIDE.md: Wait for WebSocket connection AND initial state sync
  useEffect(() => {
    if (isInitialized) {
      setLoading(false);
      // Clear any pending timeout since initialization completed successfully
    }
  }, [isInitialized, setLoading]);

  // Resume-card handoff: the dashboard's "Continue the Adventure" CTA drops
  // a one-shot sessionStorage flag and navigates here; when the lobby is
  // fully initialized and the flag is fresh (60s TTL, guards against stale
  // flags from an abandoned tab), fire startGame for the leader's party.
  // Rejections surface via the "Lobby Cancelled" notification path (see
  // game-events.ts's lobby:start-game-result case); the escape-hatch effect
  // below is a bounded backstop for the rare case nothing clears it.
  const resumeAutoStartFired = useRef(false);
  useEffect(() => {
    if (!isInitialized || !currentPartyId || resumeAutoStartFired.current) return;
    const raw = sessionStorage.getItem(RESUME_AUTO_START_KEY);
    if (!raw) return;
    resumeAutoStartFired.current = true;
    sessionStorage.removeItem(RESUME_AUTO_START_KEY);
    const age = Date.now() - Number(raw);
    if (Number.isNaN(age) || age > 60_000) {
      // Stale handoff. startGame will never fire, so drop the overlay we
      // seeded at mount and fall back to the normal interactive lobby.
      setIsGameTransitioning(false);
      return;
    }
    console.log('[resume-card] auto-starting adventure from dashboard handoff');
    websocketService.startGame(currentPartyId);
  }, [isInitialized, currentPartyId]);

  // Escape hatch for the seeded-true overlay above. Backstop for the rare
  // case where nothing clears isGameTransitioning (e.g. the rejection's
  // "Lobby Cancelled" notification predates this transition and gets
  // filtered out as stale by findFreshCancelledNotification's recency
  // window, or a genuinely no-response hang). Rather than silently reveal
  // whatever the lobby looks like at that moment (which could itself still
  // be mid-init, reintroducing the exact flash this is meant to fix),
  // surface an actionable error through the same ReconnectScreen error/retry
  // UI already used for cancellations. Armed only once initialized so a slow
  // lobby connect doesn't eat into this budget, and a generous 30s (matching
  // GameLayout's soft-stuck convention) so it doesn't clip a real,
  // merely-slow round-trip.
  useEffect(() => {
    if (!isInitialized || !isGameTransitioning || gameStartError) return;
    const timer = setTimeout(() => {
      setGameStartError('This is taking longer than expected.');
    }, 30_000);
    return () => clearTimeout(timer);
  }, [isInitialized, isGameTransitioning, gameStartError]);

  // On lobby init, patch party's finalGameTimeSeconds from localStorage (immediate)
  // then refresh from Firestore API (authoritative). Prevents stale "Day 2" in ModulePanel
  // when Firestore hasn't been updated yet by game-server's POST.
  const partyTimeRefreshed = useRef(false);
  useEffect(() => {
    if (!isInitialized || !currentPartyId || partyTimeRefreshed.current) return;
    partyTimeRefreshed.current = true;

    // Read fresh state to avoid stale closure from parties dep
    const { parties: freshParties, updateParty: update } = useLobbyStore.getState();
    const currentParty = freshParties.find((p) => p.id === currentPartyId);
    if (!currentParty?.selectedModuleId) return;

    // 1. Immediate patch from localStorage (set during session_ended)
    const lsKey = `game-session-final-time:${currentPartyId}`;
    const cached = localStorage.getItem(lsKey);
    if (cached) {
      const seconds = Number(cached);
      if (!Number.isNaN(seconds) && currentParty.lastSessionEnd) {
        update(currentPartyId, {
          lastSessionEnd: { ...currentParty.lastSessionEnd, finalGameTimeSeconds: seconds },
        });
        localStorage.removeItem(lsKey);
      }
    }

    // 2. Background refresh from Firestore (authoritative source)
    apiClient
      .getParty()
      .then(({ party }) => {
        if (!party) return;
        const raw = party.lastSessionEnd;
        if (typeof raw !== 'object' || raw === null) return;
        const lastSessionEnd = raw as NonNullable<typeof currentParty.lastSessionEnd>;
        if (lastSessionEnd) {
          useLobbyStore.getState().updateParty(currentPartyId, { lastSessionEnd });
        }
      })
      .catch(() => {
        // Non-critical — localStorage patch already covers the common case
      });
  }, [isInitialized, currentPartyId]);

  // NOTE: no frontend "send Hello to Tavian on empty chat" trigger here.
  // The lobby-server already auto-greets every first-time joiner via
  // send_tavian_greeting (context-aware: it knows character/party/recovery
  // /level-up state). A frontend auto-message duplicated it: the backend
  // greeting is a ~7s LLM call, so the empty-room check always lost the
  // race and a fresh guest got TWO Tavian welcomes. Backend greeting is
  // the single source of truth.

  // Monitor for Game Starting notification - show loading screen immediately when vote passes
  useEffect(() => {
    const gameStartingNotif = notifications.find(
      (n) => n.type === 'system' && n.title === 'Game Starting' && !n.isRead
    );

    if (gameStartingNotif) {
      console.log('[LobbyLayout] Game starting notification received:', gameStartingNotif);
      setIsGameTransitioning(true);
      setIsCreatingSession(true); // Mark as "creating" phase

      // Mark notification as read to prevent re-processing
      markNotificationRead(gameStartingNotif.id);

      // Store module info for loading screen if available
      const moduleName = gameStartingNotif.data?.moduleName;
      if (typeof moduleName === 'string') {
        localStorage.setItem('game-session-module', moduleName);
      }

      // Convert moduleId to GCS URLs for hero media if available
      const moduleId = gameStartingNotif.data?.moduleId;
      if (typeof moduleId === 'string') {
        const heroImageUrl = getModuleHeroUrl(moduleId);
        const heroVideoUrl = getModuleHeroVideoUrl(moduleId);

        console.log('[LobbyLayout] Constructed hero URLs:', {
          moduleId,
          heroImageUrl,
          heroVideoUrl,
        });

        localStorage.setItem('game-session-hero-image', heroImageUrl);
        localStorage.setItem('game-session-hero-video', heroVideoUrl);
      }

      const partyName = gameStartingNotif.data?.partyName;
      if (typeof partyName === 'string') {
        localStorage.setItem('game-session-party', JSON.stringify({ name: partyName }));
      }
    }
  }, [notifications, markNotificationRead]);

  // Monitor for Game Start — driven by `pendingGameStart` (set in game-events.ts
  // when `lobby:game-started` arrives). Watching a dedicated atom instead of
  // `notifications` prevents unrelated lobby updates (chat, presence, background
  // events) from re-firing this effect mid-flight, aborting the in-flight
  // waitForGameReady, and silently dropping the navigation.
  useEffect(() => {
    if (!pendingGameStart) return;
    const {
      gameSessionId,
      gameServerWsUrl,
      moduleName,
      moduleHeroImage,
      moduleHeroVideo,
      duelParticipants,
    } = pendingGameStart;

    const controller = new AbortController();
    void (async () => {
      // Wrap the whole flow so an unexpected throw (most realistically a
      // localStorage.setItem quota error in a constrained browser) can't leak
      // `pendingGameStart` and strand the user behind the lobby overlay with
      // no recovery path. On any failure, surface a toast and reset state.
      let navigated = false;
      try {
        const failedTime = sessionStorage.getItem('game-start-failed');
        if (failedTime) {
          const secondsSinceFailure = (Date.now() - parseInt(failedTime)) / 1000;
          sessionStorage.removeItem('game-start-failed');
          if (secondsSinceFailure < 10) {
            toast.error('Game server is temporarily unavailable. Please try again in a moment.');
            return;
          }
        }

        const gameHttpUrl = gameServerWsUrl
          .replace(/^wss:/, 'https:')
          .replace(/^ws:/, 'http:')
          .replace(/\/ws\/[^/]+$/, '')
          .replace(/\/ws$/, '');

        // Persist identity before navigating; backend session already exists
        // (lobby-server set it up before sending us `lobby:game-started`).
        localStorage.setItem('active-game-session', gameSessionId);
        localStorage.setItem('game-session-ws-url', gameServerWsUrl);

        // Removed: waitForGameReady polling (up to 41s of "Opening a portal..."
        // loader with no escape). With min-instances=1 the game-server is
        // always warm, so the polling almost never gates on a cold start —
        // and when it does, GameLayout's startSession + the new soft-stuck
        // escape (now covers connected+!gameInitialized) handle the wait
        // with a usable Reconnect / Return-to-lobby UI. Sitting in the lobby
        // staring at "Opening a portal..." with no buttons was strictly worse
        // than navigating immediately.
        //
        // Best-effort fire-and-forget warm-poke so the game-server has a
        // request in flight by the time our WS upgrade arrives. Don't await.
        void fetch(`${gameHttpUrl}/ready`, { signal: controller.signal }).catch(() => {});
        if (controller.signal.aborted) return;

        if (moduleName) localStorage.setItem('game-session-module', moduleName);
        if (moduleHeroImage) localStorage.setItem('game-session-hero-image', moduleHeroImage);
        if (moduleHeroVideo) localStorage.setItem('game-session-hero-video', moduleHeroVideo);
        if (duelParticipants) {
          // Merge with any client-side pre-write (ChatPanel /duel) so that:
          // 1. charIds set client-side are preserved (server doesn't send them)
          // 2. Server URLs win when present, client URLs fill the gap when null
          //    (e.g. session.portrait_url not yet generated server-side)
          //
          // Image fallback is gated on charId match — without that, a stale
          // localStorage from the previous duel (Tavian-driven duels don't
          // pre-write here, and `clearGameSessionState('route-hints')` on
          // duel-end leaves the key intact) would attach the prior opponent's
          // portrait to the new opponent's name. The loader then renders the
          // wrong NPC's face above the new name.
          let merged: typeof duelParticipants = duelParticipants;
          try {
            const stored = localStorage.getItem('game-session-duel-portraits');
            if (stored) {
              const prev = JSON.parse(stored) as {
                player?: { charId?: string; name?: string; imageUrl?: string };
                opponent?: { charId?: string; name?: string; imageUrl?: string };
              };
              const sameCharId = (
                a: { charId?: string } | undefined,
                b: { charId?: string } | undefined
              ) => !!a?.charId && a.charId === b?.charId;
              merged = {
                player: {
                  ...prev.player,
                  ...duelParticipants.player,
                  imageUrl:
                    duelParticipants.player?.imageUrl ??
                    (sameCharId(prev.player, duelParticipants.player)
                      ? prev.player?.imageUrl
                      : undefined),
                },
                opponent: {
                  ...prev.opponent,
                  ...duelParticipants.opponent,
                  imageUrl:
                    duelParticipants.opponent?.imageUrl ??
                    (sameCharId(prev.opponent, duelParticipants.opponent)
                      ? prev.opponent?.imageUrl
                      : undefined),
                },
              };
            }
          } catch {
            // Malformed pre-write — fall back to server payload as-is.
          }
          console.log('[duel-loader] LobbyLayout merged duelParticipants', {
            server: duelParticipants,
            merged,
          });
          localStorage.setItem('game-session-duel-portraits', JSON.stringify(merged));
          setTransitionDuelParticipants(merged);
        } else {
          // Regular adventure: clear any stale duel flag from a prior duel that
          // ended with the tab being closed/reloaded (i.e. before `/end-session`
          // ran `clearGameSessionState('all')`). gameStore's session_ending /
          // session_ended cases and game-connection-manager's 4100 close all
          // use this localStorage as the "is this a duel?" signal — without an
          // explicit clear here, the next adventure's end is misclassified:
          // status never flips to 'ending', auto-nav to /lobby is suppressed,
          // and the summary isn't persisted for Tavian's lobby recap.
          localStorage.removeItem('game-session-duel-portraits');
        }

        // Read parties at run-time instead of subscribing — avoids re-firing this
        // effect on every party update during the up-to-41s waitForGameReady window.
        const partiesNow = useLobbyStore.getState().parties;
        const derivedPartyId = gameSessionId.split('_').slice(0, -1).join('_');
        const party = partiesNow.find((p) => p.id === derivedPartyId);
        if (party) {
          localStorage.setItem(
            'game-session-party',
            JSON.stringify({ id: party.id, name: party.name || 'Your Party' })
          );
          const lastSessionSummary = party.lastSessionEnd?.summary;
          if (lastSessionSummary) {
            localStorage.setItem('game-session-last-summary', lastSessionSummary);
          }
        } else {
          localStorage.setItem(
            'game-session-party',
            JSON.stringify({ id: derivedPartyId, name: 'Your Party' })
          );
        }

        sessionStorage.setItem('game-session-fresh', '1');

        // Mark the matching unread "Game Started" notif as read so the bell
        // doesn't stay highlighted after navigation. Read at run-time to avoid
        // adding `notifications` to the effect deps.
        const notifNow = useLobbyStore
          .getState()
          .notifications.find(
            (n) =>
              n.title === 'Game Started' && !n.isRead && n.data?.gameSessionId === gameSessionId
          );
        if (notifNow) markNotificationRead(notifNow.id);

        setIsGameTransitioning(true);
        navigate(`/play/${gameSessionId}`);
        navigated = true;
      } catch (err) {
        console.error('[LobbyLayout] game-start flow failed:', err);
        toast.error('Failed to launch the game. Please try again.');
        setIsGameTransitioning(false);
      } finally {
        // Always clear the transition signal — even on abort or throw — so
        // the user isn't stranded on the loading overlay with no recovery.
        if (!navigated) setIsGameTransitioning(false);
        setIsCreatingSession(false);
        setPendingGameStart(null);
      }
    })();

    return () => controller.abort();
  }, [pendingGameStart, setPendingGameStart, navigate, markNotificationRead]);

  // Keyboard shortcut example
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key === 's') {
        event.preventDefault();
        // Alt+S keyboard shortcut - can be wired to panel actions
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Monitor for Lobby Cancelled notification - reset game transition state and
  // show error. This title is also reused by lobby:start-game-result
  // rejections (party-broadcast, so non-requesting party members receive it
  // too) and by ChatPanel's /start-adventure (which never sets
  // isGameTransitioning at all). Neither notifications ever auto-expire, so
  // the recency check below guards against a stale unread one from an
  // earlier, unrelated attempt wrongly cancelling a brand-new transition.
  useEffect(() => {
    const cancelledNotif = findFreshCancelledNotification(notifications, Date.now());

    if (cancelledNotif && isGameTransitioning) {
      console.log('[LobbyLayout] Game start cancelled:', cancelledNotif);

      // Extract error message from the notification
      const errorMessage = cancelledNotif.message || 'Failed to start game';

      // Mark as read to prevent re-processing
      markNotificationRead(cancelledNotif.id);

      // Show error state instead of just resetting
      setGameStartError(errorMessage);
      setIsCreatingSession(false);
      setTransitionDuelParticipants(undefined);

      // Clear localStorage game session data since we're not starting
      localStorage.removeItem('active-game-session');
      localStorage.removeItem('game-session-ws-url');
      localStorage.removeItem('game-session-duel-portraits');
    }
  }, [notifications, markNotificationRead, isGameTransitioning]);

  // Handler to retry game start after error
  const handleRetryGameStart = useCallback(() => {
    setGameStartError(null);
    setIsGameTransitioning(false);
    setIsCreatingSession(false);
  }, []);

  // Show disconnected overlay when reconnection has failed and we're actually offline
  // This takes priority over all other states to ensure user can reconnect
  if (reconnectFailed && !isOnline) {
    return <DisconnectedOverlay onReconnect={() => websocketService.manualReconnect()} />;
  }

  // Show loading UI until lobby is initialized AND character data is ready
  // If we have a persisted selectedCharacterId, wait for the actual character data to load
  // This prevents layout jump from 2-panel to 3-panel after initialization
  const isWaitingForCharacter = selectedCharacterId && !hasCharacter;
  if (!isInitialized || isWaitingForCharacter) {
    return (
      <LobbyOpeningPortal portalBgLoaded={portalBgLoaded} setPortalBgLoaded={setPortalBgLoaded} />
    );
  }

  return (
    <div className="flex-1 p-4 h-screen relative overflow-hidden min-w-[1024px]">
      <LobbyBackground roomContext={roomContext} />
      {/* One-time post-creation hero moment (z-40, under ReconnectScreen) */}
      <ErrorBoundary fallback={null}>
        <BeginAdventureOverlay />
      </ErrorBoundary>
      {isGameTransitioning && (
        <ReconnectScreen
          adventureName={String(
            notifications.find((n) => n.title === 'Game Starting')?.data?.moduleName ||
              notifications.find((n) => n.title === 'Game Started')?.data?.moduleName ||
              localStorage.getItem('game-session-module') ||
              'Unknown Adventure'
          )}
          heroImageUrl={localStorage.getItem('game-session-hero-image') ?? undefined}
          heroVideoUrl={localStorage.getItem('game-session-hero-video') ?? undefined}
          duelParticipants={resolvedDuelParticipants}
          message={
            isCreatingSession
              ? `Creating your adventure...`
              : resolvedDuelParticipants
                ? 'Entering the Training Grounds...'
                : 'Opening a portal to your adventure...'
          }
          error={gameStartError}
          onRetry={handleRetryGameStart}
        />
      )}
      {/* Simplified layout when no character - just Scene + Chat */}
      {!hasCharacter ? (
        <PanelGroup direction="horizontal" className="h-full">
          {/* Left: Scene Panel. The column fills the viewport: the scene
              panel absorbs the slack (its Present list scrolls).
              overflow-y-auto is the fallback for a short viewport. */}
          <Panel defaultSize={35} minSize={25}>
            <div className="flex h-full flex-col gap-4 overflow-y-auto">
              <ErrorBoundary fallback={PanelErrorFallback}>
                <ScenePanel context="lobby" className="grow" />
              </ErrorBoundary>
            </div>
          </Panel>

          <PanelResizeHandle
            className={cn(
              'w-1 bg-transparent hover:bg-border-subtle transition-colors duration-150 mx-2 flex items-center justify-center after:h-16 after:w-1 after:bg-border after:rounded-full after:hover:bg-primary after:transition-colors after:duration-150'
            )}
          />

          {/* Right: Chat Panel (main interface). Promo banner rides ON TOP of
                this column only, so it lowers the chat, not the side panels. */}
          <Panel defaultSize={65} minSize={40}>
            <div className="flex h-full flex-col">
              {!isGameTransitioning && <PromoBanner />}
              <div className="min-h-0 flex-1">
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <ChatPanel />
                </ErrorBoundary>
              </div>
            </div>
          </Panel>
        </PanelGroup>
      ) : (
        /* Full layout when player has a character */
        <PanelGroup direction="horizontal" className="h-full" onLayout={handleLayout}>
          {/* Left Column: Scene Image + Welcome (one-time, right after this
              session's character creation) + Friends. The scene panel
              absorbs the slack (its Present list scrolls) and Friends sits
              at the bottom, capped at half the column so a long list scrolls
              instead of squeezing it. Same overflow-y-auto fallback as the
              pre-character column. */}
          <Panel defaultSize={layout ? layout[0] : defaultLayout[0]} minSize={20}>
            <div className="flex h-full flex-col gap-4 overflow-y-auto">
              <ErrorBoundary fallback={PanelErrorFallback}>
                <ScenePanel context="lobby" className="grow" />
              </ErrorBoundary>
              <ErrorBoundary fallback={PanelErrorFallback}>
                <WelcomePanel />
              </ErrorBoundary>
              <div className="max-h-[50%] shrink-0 overflow-y-auto">
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <FriendsPanel />
                </ErrorBoundary>
              </div>
            </div>
          </Panel>

          <PanelResizeHandle
            className={cn(
              'w-1 bg-transparent hover:bg-border-subtle transition-colors duration-150 mx-2 flex items-center justify-center after:h-16 after:w-1 after:bg-border after:rounded-full after:hover:bg-primary after:transition-colors after:duration-150'
            )}
          />

          {/* Middle Panel: Chat (Main interface - never closeable). Promo
                banner rides ON TOP of this column only, so it lowers the chat,
                not the left/right panels. Suppressed during the game-start
                transition, when the ReconnectScreen overlay clips this column
                below the fold (an invisible banner must not log an impression). */}
          <Panel defaultSize={layout ? layout[1] : defaultLayout[1]} minSize={30}>
            <div className="flex h-full flex-col">
              {!isGameTransitioning && <PromoBanner />}
              <div className="min-h-0 flex-1">
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <ChatPanel />
                </ErrorBoundary>
              </div>
            </div>
          </Panel>

          <PanelResizeHandle
            className={cn(
              'w-1 bg-transparent hover:bg-border-subtle transition-colors duration-150 mx-2 flex items-center justify-center after:h-16 after:w-1 after:bg-border after:rounded-full after:hover:bg-primary after:transition-colors after:duration-150'
            )}
          />

          {/* Right Panel: Adventure CTA + Module Panel + Party Panel + CharDetails (all hug content) */}
          <Panel defaultSize={layout ? layout[2] : defaultLayout[2]} minSize={20}>
            <div className="h-full overflow-y-auto">
              <div className="flex flex-col gap-4">
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <AdventureCtaPanel />
                </ErrorBoundary>
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <ModulePanel />
                </ErrorBoundary>
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <PartyPanel />
                </ErrorBoundary>
                <ErrorBoundary fallback={PanelErrorFallback}>
                  <CharDetailsPanel />
                </ErrorBoundary>
              </div>
            </div>
          </Panel>
        </PanelGroup>
      )}
    </div>
  );
}

/**
 * Cold-start spinner for the lobby. Progressive messaging telegraphs
 * "the server is doing real work" — without it the static "Opening
 * Portal..." sits unchanged for 60-180s on Cloud Run cold-start, the
 * user assumes the page is broken, reloads, and lands right back here.
 */
function LobbyOpeningPortal({
  portalBgLoaded,
  setPortalBgLoaded,
}: {
  portalBgLoaded: boolean;
  setPortalBgLoaded: (loaded: boolean) => void;
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((performance.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  let label = 'Opening Portal...';
  if (elapsedSeconds >= 90) {
    label = 'Server is warming up—cold start takes ~2 min on first connect.';
  } else if (elapsedSeconds >= 45) {
    label = 'Almost there—syncing your lobby state...';
  } else if (elapsedSeconds >= 20) {
    label = 'Connecting to the lobby...';
  } else if (elapsedSeconds >= 8) {
    label = 'Opening Portal...';
  }
  return (
    <div className="flex-1 h-screen flex items-center justify-center relative">
      {/* Tavian video background */}
      <ManagedVideo
        src={getVideoUrl('tavian.mp4')}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: 'brightness(0.3)' }}
        onCanPlay={() => setPortalBgLoaded(true)}
      />
      <div className="relative z-10 flex flex-col items-center gap-4">
        <AnkhSpinner
          size="lg"
          className={`transition-colors duration-700 ${portalBgLoaded ? 'text-[#d4a574]' : 'text-accent-gold'}`}
        />
        <p
          className={`transition-colors duration-700 ${portalBgLoaded ? 'text-white/90' : 'text-foreground'}`}
        >
          {label}
        </p>
      </div>
    </div>
  );
}
